import { createContext, ReactNode, useEffect, useReducer } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  User,
  connectAuthEmulator,
} from 'firebase/auth';
import { connectFirestoreEmulator, doc, getFirestore, updateDoc } from 'firebase/firestore';
// @types
import { ActionMap, AuthState, AuthUser, FirebaseContextType } from '../@types/auth';
//
import { FIREBASE_API } from '../config';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL, connectStorageEmulator } from 'firebase/storage';
import { CustomClaims } from 'src/@types/user';

// ----------------------------------------------------------------------
const firebaseApp = initializeApp(FIREBASE_API);

const AUTH = getAuth(firebaseApp);
const DB = getFirestore(firebaseApp);
const ST = getStorage(firebaseApp);
const FUNCTIONS = getFunctions(firebaseApp, 'europe-west3');

const emaulator = process.env.NEXT_PUBLIC_EMULATOR;
if (emaulator === 'true' ) {
  connectAuthEmulator(AUTH, "http://localhost:9099"); 
  connectFirestoreEmulator(DB, 'localhost', 8080);
  connectStorageEmulator(ST, "localhost", 9199);
  connectFunctionsEmulator(FUNCTIONS, "localhost", 5001);
}

const upload = (async (preview: string, path: string): Promise<string> => {
  const upRef = ref(ST, path);
  const blob = await fetch(preview).then(r => r.blob());
  console.log(blob);

  await uploadBytes(upRef, blob);
  return await getDownloadURL(upRef);
});

const initialState: AuthState = {
  isAuthenticated: false,
  isVerified: false,
  isInitialized: false,
  user: null,
  customClaims: {},
};

enum Types {
  Initial = 'INITIALISE',
  UpdateCustomClaims = 'UPDATECUSTOMCLAIMS',
}

type FirebaseAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    isVerified: boolean;
    user: AuthUser;
    customClaims: CustomClaims;
  };
  [Types.UpdateCustomClaims]: {
    customClaims: CustomClaims;
  }
};

type FirebaseActions = ActionMap<FirebaseAuthPayload>[keyof ActionMap<FirebaseAuthPayload>];

const reducer = (state: AuthState, action: FirebaseActions) => {
  if (action.type === 'INITIALISE') {
    const { isAuthenticated, isVerified, user, customClaims } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isVerified,
      isInitialized: true,
      user,
      customClaims,
    };
  } else if (action.type === 'UPDATECUSTOMCLAIMS') {
    const { customClaims } = action.payload;
    return {
      ...state,
      customClaims,
    };
  }

  return state;
};

const AuthContext = createContext<FirebaseContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(
    () =>
      onAuthStateChanged(AUTH, async (user) => {
        if (user) {
          const customClaims = (await user.getIdTokenResult(true)).claims as CustomClaims;
          dispatch({
            type: Types.Initial,
            payload: { isAuthenticated: true, isVerified: user.emailVerified, user, customClaims },
          });
        } else {
          dispatch({
            type: Types.Initial,
            payload: { isAuthenticated: false, isVerified: false, user: null, customClaims: {} },
          });
        }
      }),
    [dispatch]
  );

  const loginGoogle = async () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(AUTH, provider);
  }

  const login = (email: string, password: string) =>
    signInWithEmailAndPassword(AUTH, email, password);

  const register = (email: string, password: string, firstName: string, lastName: string) =>
    createUserWithEmailAndPassword(AUTH, email, password).then(async (res) => {
      await updateProfile(res.user, {
        displayName: `${firstName} ${lastName}`,
        photoURL: `https://ui-avatars.com/api/?name=${firstName}+${lastName}&background=f8d8a8`,
      });
      return res;
    });

  const setProfile = async (displayName: string, photoURL: string) => {
    await updateProfile(AUTH.currentUser as User, {
      displayName, photoURL
    });
    const { brand_id } = state.customClaims;
    const { uid } = AUTH.currentUser || {};
    if (brand_id && uid) {
      const userRef = doc(DB, `brands/${brand_id}/users/${uid}`);
      await updateDoc(userRef, { name: displayName });
    }
  }

  const refreshCustomClaims = async() => {
    if (AUTH.currentUser) {
      const customClaims = (await AUTH.currentUser.getIdTokenResult(true)).claims as CustomClaims;
      dispatch({
        type: Types.UpdateCustomClaims,
        payload: { customClaims },
      });
    }
  }

  const logout = () => signOut(AUTH);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'firebase',
        refreshCustomClaims,
        setProfile,
        loginGoogle,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider, DB, ST, upload, FUNCTIONS };
